// Biblioteca de contexto (/api/client/library): tudo o que a pessoa ENVIOU (documentos e imagens anexadas no chat), com a conversa de origem.
// Apagar tem confirmação inline dizendo o que sai junto; em conta de empresa a empresa mantém o registro (exclusão lógica no servidor).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Check, FileText, Image as ImageIcon, Trash2, X } from 'lucide-react';
import { api, ApiError, when, type LibraryItem } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import '../styles/library.css';

type Tab = 'all' | 'document' | 'image';
const TABS: { id: Tab; label: string }[] = [{ id: 'all', label: 'Todos' }, { id: 'document', label: 'Documentos' }, { id: 'image', label: 'Imagens' }];
const size = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB` : `${(b / 1048576).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`);
/** A conversa de origem: `/chat/:id` ou, se foi com um agente, `/agentes/:agentId/:id`. */
const originPath = (o: NonNullable<LibraryItem['origin']>) => (o.agentId ? `/agentes/${o.agentId}/${o.chatId}` : `/chat/${o.chatId}`);

export function Library() {
  const { me } = useMeRequired();
  const [tab, setTab] = useState<Tab>('all');
  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(() => { setError(null); setItems(null); api<LibraryItem[]>('/api/client/library').then(setItems).catch((e) => setError((e as Error).message)); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (it: LibraryItem) => {
    setConfirming(null);
    try { await api(`/api/client/library/${it.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); return; } }   // 404: já tinha sido apagado em outro lugar
    setItems((cur) => (cur ?? []).filter((x) => x.id !== it.id));
    toast('Item apagado');
  };

  const shown = (items ?? []).filter((i) => tab === 'all' || i.kind === tab);
  const warn = (it: LibraryItem) => (it.kind === 'document'
    ? 'Sai junto: trechos, vínculos com agentes e compartilhamentos.'
    : it.generated ? 'A imagem sai da sua galeria de imagens geradas.'
    : me.org ? 'A imagem some da biblioteca e da conversa para você.' : 'A imagem sai da mensagem de origem (o texto fica).');

  return (
    <div className="lib-page">
      <div className="ph">
        <div>
          <span className="eyebrow">Ferramentas IA</span>
          <h1>Biblioteca</h1>
          <p>Tudo o que você enviou (documentos e imagens anexadas nas conversas, com a conversa de origem) e as imagens que você gerou.</p>
          <p className="lib-note">{me.org ? 'A empresa mantém o registro do que é apagado, e a auditoria da rede mantém o registro do processamento.' : 'Apagar um item não apaga o registro do processamento: a auditoria da rede o mantém.'}</p>
        </div>
        <div className="actions"><Link className="btn" to="/documentos">Enviar em Documentos</Link></div>
      </div>

      <div className="co-tabs" role="tablist" aria-label="Biblioteca">
        {TABS.map((t) => <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      <section aria-busy={items === null && !error}>
        {error ? (
          <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={load}>Tentar de novo</button></div>
        ) : items === null ? (
          <div className="lib-skel" aria-hidden="true"><div className="skel" /><div className="skel" /><div className="skel" /></div>
        ) : shown.length === 0 ? (
          <div className="empty"><b>Nada na biblioteca ainda</b><p>Anexe um documento ou uma imagem numa conversa, envie arquivos em Documentos ou gere imagens em Imagem.</p></div>
        ) : (
          <ul className="lib-list">
            {shown.map((it) => (
              <li key={it.id} className="lib-row">
                {it.generated
                  ? <a className="lib-thumb" href={`/api/client/images/${it.id}/file`} target="_blank" rel="noopener noreferrer" title="Abrir a imagem"><img src={`/api/client/images/${it.id}/file`} alt="" loading="lazy" /></a>
                  : <span className="lib-ico">{it.kind === 'document' ? <FileText /> : <ImageIcon />}</span>}
                <b className="lib-name" title={it.name}>{it.name}</b>
                <span className="lib-origin">{it.origin
                  ? <>Conversa: <Link to={originPath(it.origin)}>{it.origin.chatTitle ?? 'Nova conversa'}</Link></>
                  : it.generated ? <>Gerada em <Link to="/imagem">Imagem</Link></>
                  : <>Enviado em <Link to="/documentos">Documentos</Link></>}</span>
                <span className="lib-meta mono">{size(it.bytes)} · {when(it.createdAt)}</span>
                <div className="lib-acts" onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>
                  {confirming === it.id ? (
                    <>
                      <button type="button" className="danger" onClick={() => void del(it)} aria-label="Confirmar: apagar item"><Check /></button>
                      <button type="button" onClick={() => setConfirming(null)} aria-label="Cancelar" autoFocus><X /></button>
                    </>
                  ) : <button type="button" className="danger" onClick={() => setConfirming(it.id)} aria-label={`Apagar ${it.name}`} title="Apagar"><Trash2 /></button>}
                </div>
                {confirming === it.id && <p className="lib-warn" role="alert">{warn(it)} A auditoria da rede mantém o registro do processamento.</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
